"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface CarouselDotsProps {
  /** Total number of dots (slides or snap points) */
  count: number;
  /** Currently selected index */
  selectedIndex: number;
  /** Called when a dot is clicked */
  onSelect: (index: number) => void;
  className?: string;
  /**
   * Max dots rendered before switching to a sliding window that keeps the
   * active dot centered (prevents overflow on small screens). Should be odd
   * so the active dot can sit in the middle.
   */
  maxVisible?: number;
}

/**
 * Pagination dots for carousels: horizontal row of circles.
 * Inactive = light gray, active = black.
 *
 * When there are more snaps than `maxVisible`, only a window of dots is shown
 * (centered on the active dot) and the outermost dots shrink to hint that more
 * slides exist beyond the window. This keeps the strip within the viewport on
 * mobile no matter how many items the section has.
 */
export function CarouselDots({
  count,
  selectedIndex,
  onSelect,
  className,
  maxVisible = 7,
}: CarouselDotsProps) {
  if (count <= 1) return null;

  const windowed = count > maxVisible;
  const middle = Math.floor(maxVisible / 2);
  // First dot index in the visible window, clamped so we never scroll past ends.
  const firstVisible = windowed
    ? Math.min(Math.max(selectedIndex - middle, 0), count - maxVisible)
    : 0;
  const visibleCount = windowed ? maxVisible : count;

  const hasLeftOverflow = windowed && firstVisible > 0;
  const hasRightOverflow = windowed && firstVisible + maxVisible < count;

  return (
    <div
      className={cn("flex items-center justify-center gap-2", className)}
      role="tablist"
      aria-label="Carousel pagination"
    >
      {Array.from({ length: visibleCount }, (_, i) => {
        const index = firstVisible + i;
        const isActive = selectedIndex === index;

        // Shrink the outermost dot(s) when more slides exist past the window.
        let sizeClass = "h-2 w-2";
        if (hasLeftOverflow && i === 0) sizeClass = "h-1 w-1";
        else if (hasLeftOverflow && i === 1) sizeClass = "h-1.5 w-1.5";
        if (hasRightOverflow && i === visibleCount - 1) sizeClass = "h-1 w-1";
        else if (hasRightOverflow && i === visibleCount - 2)
          sizeClass = "h-1.5 w-1.5";

        return (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`Go to slide ${index + 1}`}
            className={cn(
              "shrink-0 rounded-full transition-all duration-300 cursor-pointer border-0 p-0",
              sizeClass,
              isActive ? "bg-black" : "bg-gray-300 hover:bg-gray-400",
            )}
            onClick={() => onSelect(index)}
          />
        );
      })}
    </div>
  );
}
